import { RestaurantLeave } from "src/business/restaurant/leave";
import { RestaurantLeaveData } from "src/data/types/leave";
import { API_URL } from "src/Utils/API";
import { mapRestaurantLeaveDataToRestaurantLeave } from "../mappers/restaurantLeaves";
import { useMutation, useQuery } from "@tanstack/react-query";

const getRestaurantLeavesQueryKey = (restaurantId: string, startDate: string, endDate: string) => ["restaurant", restaurantId, "leaves", startDate, endDate];

export const useGetRestaurantLeaves = (restaurantId: string, startDate: string, endDate: string) => {
    return useQuery({
        queryKey: getRestaurantLeavesQueryKey(restaurantId, startDate, endDate),
        queryFn: async (): Promise<RestaurantLeave[]> => {
            const response = await fetch(`${API_URL}/restaurants/${restaurantId}/leaves?startDate=${startDate}&endDate=${endDate}`, {
                credentials: "include",
            });
            const data = await response.json();
            if (data.success) {
                return data.leaves.map((leaveData: RestaurantLeaveData) => mapRestaurantLeaveDataToRestaurantLeave(leaveData));
            } else {
                throw new Error(data.error || "Failed to fetch restaurant leaves");
            }
        },
    });
}

/** Bulk-add multiple leave dates in a single request. */
export const useAddRestaurantLeavesBulk = (restaurantId: string) => {
    return useMutation({
        mutationFn: async (dates: string[]) => {
            const response = await fetch(`${API_URL}/restaurants/${restaurantId}/leaves`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(dates),
            });
            const data = await response.json();

            if (!data.success) {
                throw new Error(data.error || "Failed to bulk add restaurant leaves");
            }
        },
    });
}

/** Delete multiple leaves in a single request. */
export const useDeleteRestaurantLeavesBulk = (restaurantId: string) => {
    return useMutation({
        mutationFn: async (leaveIds: string[]) => {
            const response = await fetch(`${API_URL}/restaurants/${restaurantId}/leaves`, {
                method: "DELETE",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(leaveIds),
            });
            const data = await response.json();
            if (!data.success) {
                throw new Error(data.error || "Failed to bulk delete restaurant leaves");
            }
        },
    });
}